// src/components/tasks/TasksPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container,
  Paper,
  Grid,
  Button, 
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
  useMediaQuery,
  useTheme,
  Fade,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Divider,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  SwipeableDrawer
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Search,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Clock,
  X,
  CheckSquare,
  Filter,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  MoreVertical,
  AlertCircle
} from 'lucide-react';
import agthealthService from '../../../services/agthealthService';
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';

// Definir color dorado personalizado
const goldColor = '#E3AE08';
const DrawerWidth = 240;

// Definir el componente Main para gestionar el espaciado con el sidebar
const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  paddingBottom: theme.spacing(16), // Espacio inferior grande (~128px)
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    paddingTop: 0,
    paddingBottom: theme.spacing(14), // Espacio inferior también en móvil (~112px)
  }
}));

// Componentes estilizados para el estilo Apple
const AppleDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(3px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '14px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: theme.spacing(1),
    maxWidth: '500px',
    width: '100%',
    margin: theme.spacing(1)
  }
}));

const AppleDialogTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2, 2, 1),
  '& h2': {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#1d1d1f'
  }
}));

const AppleDialogContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2, 1),
  }
}));

const AppleDialogActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  padding: theme.spacing(1, 2, 2),
  gap: theme.spacing(1)
}));

const AppleButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.2),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  backgroundColor: variant === 'contained' ? goldColor : 'transparent',
  color: variant === 'contained' ? 'white' : goldColor,
  border: variant === 'outlined' ? `1px solid ${goldColor}` : 'none',
  '&:hover': {
    backgroundColor: variant === 'contained' 
      ? '#c79b07' 
      : 'rgba(227, 174, 8, 0.1)',
  },
}));

// Contenedor de búsqueda estilizado
const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: theme.spacing(2),
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  }
}));

// Tabla personalizada sin sombra, solo con borde
const BorderedTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(224, 224, 224, 1)',
  boxShadow: 'none',
  overflow: 'hidden',
}));

// Chip de prioridad estilizado
const PriorityChip = styled(Chip)(({ priority }) => {
  const getColor = () => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return goldColor;
      case 'low': return '#4CAF50';
      default: return goldColor;
    }
  };
  
  return {
    backgroundColor: getColor(),
    color: 'white',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    textTransform: 'capitalize',
    borderRadius: '12px',
  };
});

// Tarjeta de tarea para vista móvil
const TaskCard = styled(Card)(({ theme, completed }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  opacity: completed ? 0.7 : 1,
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
  }
}));

// Tabs estilizados
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  minWidth: 'auto',
  color: '#666',
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    padding: theme.spacing(0.75),
  },
  '&.Mui-selected': {
    color: goldColor,
  }
}));

// Botón de filtro estilizado
const FilterButton = styled(Button)(({ theme, active }) => ({
  color: active ? goldColor : '#666',
  borderRadius: '12px',
  backgroundColor: active ? 'rgba(227, 174, 8, 0.08)' : 'transparent',
  textTransform: 'none',
  py: 1,
  px: 2,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(227, 174, 8, 0.15)',
  }
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(227, 174, 8, 0.1)',
  color: goldColor,
  fontWeight: 500,
  border: '1px solid rgba(227, 174, 8, 0.2)',
  borderRadius: '20px',
  '& .MuiChip-deleteIcon': {
    color: goldColor,
    '&:hover': {
      color: '#c79b07',
    },
  },
}));

const TasksPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para el drawer
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState({
    firstName: 'Dr. Smith',
    lastName: 'Johnson',
    specialty: 'Cardiologist'
  });
  
  // Estados para la gestión de tareas
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dueDate', direction: 'asc' });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Estado para nueva/editar tarea
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0], // Formato YYYY-MM-DD
    dueTime: '12:00', // Formato HH:MM
  });
  
  // Estados para filtros avanzados
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    priority: '',
    dateFrom: '',
    dateTo: '',
  });
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Estado para menú de acciones en móvil
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  
  // Cargar tareas al iniciar
  useEffect(() => {
    fetchTasks();
    
    // Cargar perfil de usuario si es necesario
    const fetchUserProfile = async () => {
      try {
        const response = await agthealthService.getUserProfile();
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error al cargar perfil de usuario:', error);
      }
    };
    
    fetchUserProfile();
  }, []);
  
  // Ajustar filas por página según el tamaño de pantalla
  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);
  
  // Filtrar tareas cuando cambian los filtros
  useEffect(() => {
    filterTasks();
  }, [tasks, tabValue, searchQuery, sortConfig, activeFilters]);
  
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await agthealthService.getUserTasks();
      
      if (response.success) {
        setTasks(response.data || []);
      } else {
        setNotification({
          open: true,
          message: 'No se pudieron cargar las tareas',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar tareas:', error);
      setNotification({
        open: true,
        message: 'Error al conectar con el servidor',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const filterTasks = () => {
    let filtered = [...tasks];
    
    // Filtrar por tab
    if (tabValue === 'pending') {
      filtered = filtered.filter(task => !task.completed);
    } else if (tabValue === 'completed') {
      filtered = filtered.filter(task => task.completed);
    }
    
    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        task.title.toLowerCase().includes(query) || 
        (task.description && task.description.toLowerCase().includes(query))
      );
    }
    
    // Aplicar filtros activos
    if (activeFilters.length > 0) {
      filtered = filtered.filter(task => {
        return activeFilters.every(filter => {
          switch (filter.type) {
            case 'priority':
              return task.priority === filter.value;
            case 'dateFrom':
              return !task.dueDate || new Date(task.dueDate) >= new Date(filter.value);
            case 'dateTo':
              return !task.dueDate || new Date(task.dueDate) <= new Date(filter.value);
            default:
              return true;
          }
        });
      });
    }
    
    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Manejar valores especiales
      if (sortConfig.key === 'dueDate') {
        aValue = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        bValue = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      } else if (sortConfig.key === 'priority') {
        const priorityValues = { 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityValues[a.priority] || 0;
        bValue = priorityValues[b.priority] || 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredTasks(filtered);
  };
  
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  
  const handleToggleComplete = async (taskId, currentStatus) => {
    try {
      // Optimistic UI update
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.taskId === taskId 
            ? { ...task, completed: !currentStatus } 
            : task
        )
      );
      
      // Actualizar en el backend
      await agthealthService.toggleTaskCompletion(taskId, !currentStatus);
      
      // Mostrar notificación
      setNotification({
        open: true,
        message: `Tarea marcada como ${!currentStatus ? 'completada' : 'pendiente'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al actualizar estado de tarea:', error);
      
      // Revertir cambio en caso de error
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.taskId === taskId 
            ? { ...task, completed: currentStatus } 
            : task
        )
      );
      
      // Mostrar error
      setNotification({
        open: true,
        message: 'Error al actualizar la tarea',
        severity: 'error'
      });
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta tarea?')) {
      return;
    }
    
    try {
      // Optimistic UI update
      const taskToDelete = tasks.find(t => t.taskId === taskId);
      setTasks(prevTasks => prevTasks.filter(task => task.taskId !== taskId));
      
      // Eliminar en el backend
      const response = await agthealthService.deleteTask(taskId);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'Tarea eliminada correctamente',
          severity: 'success'
        });
      } else {
        throw new Error(response.message || 'Error al eliminar la tarea');
      }
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      
      // Revertir cambio en caso de error
      fetchTasks();
      
      setNotification({
        open: true,
        message: 'Error al eliminar la tarea',
        severity: 'error'
      });
    }
  };
  
  const openEditDialog = (task) => {
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      dueDate: task.dueDate || new Date().toISOString().split('T')[0],
      dueTime: task.dueTime || '12:00'
    });
    
    setEditingTask(task);
    setOpenDialog(true);
  };
  
  const openCreateDialog = () => {
    setTaskForm({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: new Date().toISOString().split('T')[0],
      dueTime: '12:00'
    });
    
    setEditingTask(null);
    setOpenDialog(true);
  };
  
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setTaskForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      setNotification({
        open: true,
        message: 'El título de la tarea es obligatorio',
        severity: 'warning'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const taskData = {
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate,
        dueTime: taskForm.dueTime
      };
      
      let response;
      
      if (editingTask) {
        // Actualizar tarea existente
        response = await agthealthService.updateTask(editingTask.taskId, taskData);
      } else {
        // Crear nueva tarea
        taskData.completed = false;
        response = await agthealthService.createTask(taskData);
      }
      
      if (response.success) {
        // Cerrar diálogo
        setOpenDialog(false);
        
        // Mostrar notificación de éxito
        setNotification({
          open: true,
          message: editingTask 
            ? 'Tarea actualizada con éxito' 
            : 'Tarea creada con éxito',
          severity: 'success'
        });
        
        // Recargar tareas
        fetchTasks();
      } else {
        throw new Error(response.message || 'Error al guardar la tarea');
      }
    } catch (error) {
      console.error('Error al guardar tarea:', error);
      setNotification({
        open: true,
        message: 'Error al guardar la tarea',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };
  
  // Funciones para la navegación y gestión del drawer
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };
  
  const handleSettings = () => {
    navigate('/settings');
  };
  
  // Manejo de filtros avanzados
  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value
    });
  };
  
  const handleApplyFilter = (filterType) => {
    const filterValue = filters[filterType];
    if (!filterValue) return;

    // Verificar si el filtro ya existe
    const filterExists = activeFilters.some(
      filter => filter.type === filterType && filter.value === filterValue
    );

    if (!filterExists) {
      let filterLabel = '';
      switch (filterType) {
        case 'priority':
          filterLabel = `Prioridad: ${filterValue === 'high' ? 'Alta' : filterValue === 'medium' ? 'Media' : 'Baja'}`;
          break;
        case 'dateFrom':
          filterLabel = `Desde: ${new Date(filterValue).toLocaleDateString()}`;
          break;
        case 'dateTo':
          filterLabel = `Hasta: ${new Date(filterValue).toLocaleDateString()}`;
          break;
        default:
          filterLabel = `${filterType}: ${filterValue}`;
      }

      setActiveFilters([...activeFilters, { 
        type: filterType, 
        value: filterValue,
        label: filterLabel
      }]);

      // Limpiar el campo de filtro después de aplicarlo
      setFilters({
        ...filters,
        [filterType]: ''
      });
    }
  };

  const handleRemoveFilter = (indexToRemove) => {
    setActiveFilters(activeFilters.filter((_, index) => index !== indexToRemove));
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilters({
      priority: '',
      dateFrom: '',
      dateTo: '',
    });
  };
  
  // Formato de fecha para mostrar
  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'No definida';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };
  
  // Manejo del menú de acciones para móvil
  const handleOpenActionMenu = (taskId) => {
    setSelectedTaskId(taskId);
    setActionMenuOpen(true);
  };
  
  const handleCloseActionMenu = () => {
    setActionMenuOpen(false);
    setSelectedTaskId(null);
  };
  
  // Obtener tarea seleccionada
  const getSelectedTask = () => {
    return tasks.find(task => task.taskId === selectedTaskId) || null;
  };
  
  // Render de la página
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onLogout={handleLogout}
        activeSection="tasks" // Importante: indica que estamos en la sección de tareas
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title="Tareas"
          user={user} 
          isMobile={isMobile} 
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2, md: 3 } }}>
          {/* Sección de búsqueda y filtros */}
          <SearchContainer>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              {/* Campo de búsqueda con título por encima */}
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 500, 
                  color: '#424242',
                  fontSize: '0.875rem'
                }}
              >
                Buscar tarea
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Título o descripción..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color={goldColor} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={handleClearSearch} 
                        size="small"
                        sx={{ 
                          color: '#999',
                          '&:hover': { color: '#666' }
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                      boxShadow: 'none',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    }
                  }
                }}
                sx={{ mb: 2 }}
              />
              
              {/* Botón de filtros */}
              <FilterButton
                startIcon={<Filter size={isSmall ? 16 : 18} />}
                endIcon={showFilters ? <ChevronUp size={isSmall ? 14 : 16} /> : <ChevronDown size={isSmall ? 14 : 16} />}
                onClick={handleToggleFilters}
                active={showFilters || activeFilters.length > 0}
                size={isSmall ? "small" : "medium"}
              >
                {isSmall ? "Filtros" : "Filtros avanzados"}
              </FilterButton>
            </Box>

            {/* Filtros expandibles con estilo Apple */}
            <Collapse in={showFilters}>
              <Box 
                mt={2} 
                p={isSmall ? 2 : 3} 
                sx={{
                  backgroundColor: '#f5f5f7',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.05)'
                }}
              >
                <Grid container spacing={isSmall ? 2 : 3}>
                  {/* Filtro de prioridad */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Prioridad
                    </Typography>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: `rgba(227, 174, 8, 0.5)`,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: goldColor,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    >
                      <Select
                        name="priority"
                        value={filters.priority}
                        onChange={handleFilterChange}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Seleccionar</em></MenuItem>
                        <MenuItem value="high">Alta</MenuItem>
                        <MenuItem value="medium">Media</MenuItem>
                        <MenuItem value="low">Baja</MenuItem>
                      </Select>
                    </FormControl>
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('priority')}
                        disabled={!filters.priority}
                        sx={{ 
                          color: goldColor,
                          textTransform: 'none',
                          fontWeight: 500,
                          '&.Mui-disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                          }
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Fecha desde */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Fecha desde
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="dateFrom"
                      type="date"
                      value={filters.dateFrom}
                      onChange={handleFilterChange}
                      InputProps={{ 
                        sx: {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: `rgba(227, 174, 8, 0.5)`,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: goldColor,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    />
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('dateFrom')}
                        disabled={!filters.dateFrom}
                        sx={{ 
                          color: goldColor,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Fecha hasta */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
                    >
                      Fecha hasta
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="dateTo"
                      type="date"
                      value={filters.dateTo}
                      onChange={handleFilterChange}
                      InputProps={{ 
                        sx: {
                          borderRadius: '12px',
                          backgroundColor: '#fff',
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: `rgba(227, 174, 8, 0.5)`,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: goldColor,
                            borderWidth: '1px',
                          },
                        }
                      }}
                    />
                    <Box mt={1}>
                      <Button 
                        onClick={() => handleApplyFilter('dateTo')}
                        disabled={!filters.dateTo}
                        sx={{ 
                          color: goldColor,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {/* Chips de filtros activos con estilo Apple */}
            {activeFilters.length > 0 && (
              <Box 
                mt={2} 
                display="flex" 
                flexWrap="wrap" 
                alignItems="center"
                sx={{ py: 1 }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666', 
                    mr: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Filtros:
                </Typography>
                {activeFilters.map((filter, index) => (
                  <FilterChip
                    key={index}
                    label={filter.label}
                    onDelete={() => handleRemoveFilter(index)}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
                <Button 
                  size="small" 
                  onClick={handleClearAllFilters} 
                  sx={{ 
                    ml: 1,
                    mb: 1, 
                    color: '#666',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: goldColor,
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Limpiar
                </Button>
              </Box>
            )}
          </SearchContainer>

          {/* Tabs y botón de nueva tarea */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              variant={isSmall ? "fullWidth" : "standard"}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                flexGrow: 1,
                maxWidth: { xs: '100%', sm: '60%' },
                '& .MuiTabs-indicator': {
                  backgroundColor: goldColor
                }
              }}
            >
              <StyledTab value="all" label="Todas" />
              <StyledTab value="pending" label="Pendientes" />
              <StyledTab value="completed" label="Completadas" />
            </Tabs>
            
            <AppleButton 
              variant="contained" 
              startIcon={!isSmall && <Plus size={18} />}
              onClick={openCreateDialog}
              size={isSmall ? "small" : "medium"}
              sx={{ minWidth: isSmall ? '40px' : 'auto' }}
            >
              {isSmall ? <Plus size={18} /> : "Nueva Tarea"}
            </AppleButton>
          </Box>

          {loading && tasks.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: goldColor }} />
            </Box>
          ) : filteredTasks.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6, 
              backgroundColor: 'rgba(227, 174, 8, 0.05)', 
              borderRadius: '12px',
              border: '1px solid rgba(227, 174, 8, 0.1)',
              px: 3
            }}>
              <CheckSquare size={48} color={goldColor} style={{ opacity: 0.6, marginBottom: 16 }} />
              <Typography variant="h6" color={goldColor} sx={{ mb: 1, fontWeight: 500 }}>
                No se encontraron tareas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchQuery || activeFilters.length > 0 
                  ? "No se encontraron tareas con los criterios de búsqueda actuales."
                  : tabValue === 'completed' 
                    ? "No tienes tareas completadas."
                    : tabValue === 'pending'
                      ? "No tienes tareas pendientes."
                      : "No tienes tareas registradas. Comienza creando tu primera tarea."
                }
              </Typography>
              <AppleButton 
                variant="contained" 
                startIcon={<Plus size={18} />}
                onClick={openCreateDialog}
              >
                Crear nueva tarea
              </AppleButton>
            </Box>
          ) : (
            <>
              {/* Vista de escritorio: Tabla */}
              {!isMobile && (
                <>
                  <BorderedTableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(227, 174, 8, 0.08)' }}>
                          <TableCell width="50" padding="checkbox" align="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                              Estado
                            </Typography>
                          </TableCell>
                          <TableCell 
                            onClick={() => handleSort('title')}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                Título
                              </Typography>
                              {sortConfig.key === 'title' && (
                                sortConfig.direction === 'asc' ? 
                                <ArrowUp size={16} style={{ marginLeft: 4 }} /> : 
                                <ArrowDown size={16} style={{ marginLeft: 4 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell 
                            onClick={() => handleSort('priority')}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                Prioridad
                              </Typography>
                              {sortConfig.key === 'priority' && (
                                sortConfig.direction === 'asc' ? 
                                <ArrowUp size={16} style={{ marginLeft: 4 }} /> : 
                                <ArrowDown size={16} style={{ marginLeft: 4 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell 
                            onClick={() => handleSort('dueDate')}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                Fecha límite
                              </Typography>
                              {sortConfig.key === 'dueDate' && (
                                sortConfig.direction === 'asc' ? 
                                <ArrowUp size={16} style={{ marginLeft: 4 }} /> : 
                                <ArrowDown size={16} style={{ marginLeft: 4 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                              Hora
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                              Acciones
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTasks
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((task) => (
                            <TableRow 
                              key={task.taskId}
                              sx={{ 
                                opacity: task.completed ? 0.7 : 1,
                                '&:hover': { 
                                  backgroundColor: 'rgba(227, 174, 8, 0.04)',
                                }
                              }}
                            >
                              <TableCell padding="checkbox" align="center">
                                <Checkbox
                                  checked={task.completed || false}
                                  onChange={() => handleToggleComplete(task.taskId, task.completed)}
                                  icon={<XCircle size={20} color="#999" />}
                                  checkedIcon={<CheckCircle size={20} color={goldColor} />}
                                  sx={{ 
                                    color: '#999',
                                    '&.Mui-checked': {
                                      color: goldColor,
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ 
                                textDecoration: task.completed ? 'line-through' : 'none',
                                fontWeight: 500
                              }}>
                                {task.title}
                                {task.description && (
                                  <Typography 
                                    variant="caption" 
                                    display="block" 
                                    color="text.secondary"
                                    sx={{ mt: 0.5, textDecoration: 'none' }}
                                  >
                                    {task.description.substring(0, 60)}
                                    {task.description.length > 60 ? '...' : ''}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <PriorityChip 
                                  label={
                                    task.priority === 'high' ? 'Alta' : 
                                    task.priority === 'medium' ? 'Media' : 'Baja'
                                  }
                                  priority={task.priority || 'medium'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatDateDisplay(task.dueDate)}</TableCell>
                              <TableCell>{task.dueTime || '-'}</TableCell>
                              <TableCell align="right">
                                <IconButton 
                                  size="small"
                                  onClick={() => openEditDialog(task)}
                                  sx={{ color: goldColor }}
                                >
                                  <Edit size={18} />
                                </IconButton>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDeleteTask(task.taskId)}
                                  sx={{ color: '#f44336' }}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </BorderedTableContainer>
                  
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredTasks.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => 
                      `${from}-${to} de ${count}`
                    }
                    sx={{
                      '.MuiTablePagination-select': {
                        borderRadius: '8px',
                      },
                      '.MuiTablePagination-selectIcon': {
                        color: goldColor
                      },
                      '.MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          color: goldColor,
                        }
                      }
                    }}
                  />
                </>
              )}
              
              {/* Vista móvil: Tarjetas */}
              {isMobile && (
                <>
                  <Box sx={{ mb: 2 }}>
                    {filteredTasks
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((task) => (
                        <TaskCard key={task.taskId} completed={task.completed}>
                          <CardContent sx={{ pb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                                <Checkbox
                                  checked={task.completed || false}
                                  onChange={() => handleToggleComplete(task.taskId, task.completed)}
                                  icon={<XCircle size={20} color="#999" />}
                                  checkedIcon={<CheckCircle size={20} color={goldColor} />}
                                  sx={{ 
                                    padding: '4px',
                                    color: '#999',
                                    '&.Mui-checked': {
                                      color: goldColor,
                                    }
                                  }}
                                />
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 500,
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                    opacity: task.completed ? 0.7 : 1,
                                    flexGrow: 1,
                                    pr: 1
                                  }}
                                >
                                  {task.title}
                                </Typography>
                              </Box>
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenActionMenu(task.taskId)}
                                sx={{ padding: '4px' }}
                              >
                                <MoreVertical size={18} />
                              </IconButton>
                            </Box>
                            
                            {task.description && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  mt: 0.5, 
                                  mb: 1,
                                  pl: 4,
                                  opacity: task.completed ? 0.7 : 1
                                }}
                              >
                                {task.description.substring(0, 80)}
                                {task.description.length > 80 ? '...' : ''}
                              </Typography>
                            )}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 4, mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Calendar size={14} color="#666" />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateDisplay(task.dueDate)}
                                </Typography>
                              </Box>
                              
                              {task.dueTime && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Clock size={14} color="#666" />
                                  <Typography variant="caption" color="text.secondary">
                                    {task.dueTime}
                                  </Typography>
                                </Box>
                              )}
                              
                              <PriorityChip 
                                label={
                                  task.priority === 'high' ? 'Alta' : 
                                  task.priority === 'medium' ? 'Media' : 'Baja'
                                }
                                priority={task.priority || 'medium'}
                                size="small"
                                sx={{ height: 20 }}
                              />
                            </Box>
                          </CardContent>
                        </TaskCard>
                      ))}
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TablePagination
                      component="div"
                      count={filteredTasks.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10]}
                      labelRowsPerPage="Por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{
                        '.MuiTablePagination-selectIcon': {
                          color: goldColor
                        },
                        '.MuiTablePagination-actions': {
                          '& .MuiIconButton-root': {
                            color: goldColor,
                          }
                        }
                      }}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Container>
      </Main>
      
      {/* Modal para crear/editar tarea con estilo Apple */}
      <AppleDialog
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        TransitionComponent={Fade}
        transitionDuration={300}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmall}
      >
        <AppleDialogTitle>
          <Typography variant="h2">
            {editingTask ? 'Editar tarea' : 'Nueva tarea'}
          </Typography>
          <IconButton 
            onClick={() => setOpenDialog(false)}
            sx={{ 
              position: 'absolute', 
              right: 8, 
              top: 8,
              color: '#666'
            }}
          >
            <X size={20} />
          </IconButton>
        </AppleDialogTitle>
        
        <AppleDialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography 
                variant="subtitle2" 
                sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
              >
                Título
              </Typography>
              <TextField
                fullWidth
                name="title"
                value={taskForm.title}
                onChange={handleFormInputChange}
                required
                placeholder="Ej: Revisar resultados de laboratorio"
                InputProps={{ 
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography 
                variant="subtitle2" 
                sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
              >
                Descripción
              </Typography>
              <TextField
                fullWidth
                name="description"
                value={taskForm.description}
                onChange={handleFormInputChange}
                multiline
                rows={3}
                placeholder="Detalles adicionales de la tarea..."
                InputProps={{ 
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="subtitle2" 
                sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
              >
                Prioridad
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="priority"
                  value={taskForm.priority}
                  onChange={handleFormInputChange}
                  sx={{ 
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    },
                    '& .MuiSelect-icon': {
                      color: goldColor
                    }
                  }}
                >
                  <MenuItem value="low">Baja</MenuItem>
                  <MenuItem value="medium">Media</MenuItem>
                  <MenuItem value="high">Alta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="subtitle2" 
                sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
              >
                Fecha límite
              </Typography>
              <TextField
                fullWidth
                name="dueDate"
                type="date"
                value={taskForm.dueDate}
                onChange={handleFormInputChange}
                InputProps={{ 
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="subtitle2" 
                sx={{ mb: 0.5, fontWeight: 500, color: '#424242' }}
              >
                Hora
              </Typography>
              <TextField
                fullWidth
                name="dueTime"
                type="time"
                value={taskForm.dueTime}
                onChange={handleFormInputChange}
                InputProps={{ 
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f5f5f7',
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      backgroundColor: '#eaeaec',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: `0 0 0 2px rgba(227, 174, 8, 0.3)`,
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </AppleDialogContent>
        
        <AppleDialogActions>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ 
              color: '#666',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancelar
          </Button>
          <AppleButton 
            variant="contained" 
            onClick={handleSaveTask}
            disabled={loading || !taskForm.title.trim()}
          >
            {loading ? 'Guardando...' : editingTask ? 'Actualizar' : 'Crear tarea'}
          </AppleButton>
        </AppleDialogActions>
      </AppleDialog>
      
      {/* Menú de acciones para móvil (bottom drawer) */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={actionMenuOpen}
          onClose={handleCloseActionMenu}
          onOpen={() => {}}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxWidth: '100%',
              width: '100%',
            },
          }}
        >
          <Box sx={{ py: 1 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                textAlign: 'center', 
                fontWeight: 600,
                py: 1,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative'
              }}
            >
              Acciones
              <IconButton
                onClick={handleCloseActionMenu}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                <X size={18} />
              </IconButton>
            </Typography>
            
            <List sx={{ pt: 0 }}>
              {getSelectedTask() && (
                <>
                  <ListItem 
                    button 
                    onClick={() => {
                      const task = getSelectedTask();
                      handleToggleComplete(task.taskId, task.completed);
                      handleCloseActionMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getSelectedTask().completed ? (
                        <XCircle size={20} color="#f44336" />
                      ) : (
                        <CheckCircle size={20} color={goldColor} />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={getSelectedTask().completed ? "Marcar como pendiente" : "Marcar como completada"} 
                    />
                  </ListItem>
                  
                  <ListItem 
                    button 
                    onClick={() => {
                      const task = getSelectedTask();
                      openEditDialog(task);
                      handleCloseActionMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Edit size={20} color={goldColor} />
                    </ListItemIcon>
                    <ListItemText primary="Editar tarea" />
                  </ListItem>
                  
                  <ListItem 
                    button 
                    onClick={() => {
                      const task = getSelectedTask();
                      handleDeleteTask(task.taskId);
                      handleCloseActionMenu();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Trash2 size={20} color="#f44336" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Eliminar tarea" 
                      primaryTypographyProps={{ sx: { color: '#f44336' } }}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        </SwipeableDrawer>
      )}
      
      {/* Notificaciones */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={5000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ 
          vertical: isMobile ? 'top' : 'bottom', 
          horizontal: 'center' 
        }}
        sx={{ bottom: isMobile ? 'auto' : 16 }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            ...(notification.severity === 'success' && {
              backgroundColor: 'rgba(227, 174, 8, 0.9)',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            })
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Botón flotante para agregar nueva tarea (solo en móvil) */}
      {isMobile && !openDialog && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={openCreateDialog}
            sx={{
              minWidth: 0,
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: goldColor,
              boxShadow: '0 4px 10px rgba(227, 174, 8, 0.4)',
              '&:hover': {
                backgroundColor: '#c79b07',
              }
            }}
          >
            <Plus size={24} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TasksPage;